import {
  Box,
  VStack,
  Heading,
  Text,
  Code,
  Container,
  Card,
  CardBody,
  SimpleGrid,
  Link,
  useColorModeValue,
  Select,
  Button,
  HStack,
  Spinner,
} from '@chakra-ui/react';
import { PageMeta } from '../components/PageMeta';
import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';

const ApiDocumentation = () => {
  const { t } = useTranslation();
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const codeBg = useColorModeValue('gray.100', 'gray.600');

  const puzzleTypes = ['222', '333', '444', '555', '666', '777', 'pyram', 'skewb', 'minx', 'sq1', 'clock'];

  const [selectedPuzzle, setSelectedPuzzle] = useState('333');
  const [testResult, setTestResult] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleTestApi = useCallback(async () => {
    setIsLoading(true);
    setTestResult('');
    try {
      const response = await fetch(`/api/scramble?puzzleType=${selectedPuzzle}`);
      const contentType = response.headers.get('content-type');

      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('API server is not available. Make sure the backend server is running on port 3001.');
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'An unknown error occurred');
      }
      setTestResult(JSON.stringify(data, null, 2));
    } catch (error) {
      if (error instanceof Error) {
        setTestResult(`An error occurred: ${error.message}`);
      } else {
        setTestResult('An error occurred while fetching the scramble.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedPuzzle]);

  return (
    <>
      <PageMeta title="API Documentation" description="API documentation for generating scrambles." />
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          <Heading as="h1" size="xl" textAlign="center">
            {t('api.title')}
          </Heading>

          <Text fontSize="lg" textAlign="center">
            {t('api.description')}
          </Text>

          <Card bg={cardBg}>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading as="h2" size="lg">
                  {t('api.testItOutTitle')}
                </Heading>
                <Text>{t('api.testItOutDescription')}</Text>
                <HStack>
                  <Select value={selectedPuzzle} onChange={(e) => setSelectedPuzzle(e.target.value)}>
                    {puzzleTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Select>
                  <Button onClick={handleTestApi} isLoading={isLoading} colorScheme="brand">
                    {t('api.getScramble')}
                  </Button>
                </HStack>
                {isLoading && <Spinner />}
                {testResult && (
                  <Code p={4} borderRadius="md" display="block" whiteSpace="pre" bg={codeBg}>
                    {testResult}
                  </Code>
                )}
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg}>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading as="h2" size="lg">
                  {t('api.endpointTitle')}
                </Heading>
                <Text>{t('api.endpointDescription')}</Text>
                <Code p={4} borderRadius="md" bg={codeBg}>
                  GET /api/scramble
                </Code>
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg}>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading as="h2" size="lg">
                  {t('api.queryParametersTitle')}
                </Heading>
                <Text>
                  <Code>puzzleType</Code> ({t('api.required')}) - {t('api.puzzleTypeDescription')}
                </Text>
                <Text>{t('api.supportedPuzzleTypes')}:</Text>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
                  {puzzleTypes.map((type) => (
                    <Code key={type} p={2} borderRadius="md" textAlign="center" bg={codeBg}>
                      {type}
                    </Code>
                  ))}
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg}>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading as="h2" size="lg">
                  {t('api.exampleRequestTitle')}
                </Heading>
                <Code p={4} borderRadius="md" bg={codeBg}>
                  <Link href="/api/scramble?puzzleType=333" isExternal>
                    /api/scramble?puzzleType=333
                  </Link>
                </Code>
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg}>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading as="h2" size="lg">
                  {t('api.successResponseTitle')}
                </Heading>
                <Text>{t('api.successResponseDescription')}</Text>
                <Code p={4} borderRadius="md" display="block" whiteSpace="pre" bg={codeBg}>
                  {`{
  "scramble": "R' U' F R' U' R U R' F' R U' R' U' R U' F R F' R U2 R'"
}`}
                </Code>
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg}>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading as="h2" size="lg">
                  {t('api.errorResponseTitle')}
                </Heading>
                <Text>{t('api.errorResponseDescription')}</Text>
                <Code p={4} borderRadius="md" display="block" whiteSpace="pre" bg={codeBg}>
                  {`{
  "error": "Invalid or missing puzzleType"
}`}
                </Code>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </>
  );
};

export default ApiDocumentation; 